import PropTypes from "prop-types";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../parts/modal-new";
import {
  canEditRecipeIngredient,
  getRecipePortion,
  scaleRecipeIngredientNutrients,
} from "../../../../../utils/nutrition-util";

// Ingredient values are small, so keep 1 decimal (whole numbers would hide 0.4g and skew scaled values)
const formatNutrient = (value) => Math.round((Number(value) || 0) * 10) / 10;
const round2 = (value) => Math.round(value * 100) / 100;
// Display only: untouched amounts are never sent, so this rounding doesn't reach the recipe
const roundAmount = (value) => Math.round(value * 10) / 10;
// Typed amounts: digits with an optional "." decimal part
const AMOUNT_PATTERN = /^\d*\.?\d*$/;
// The API falls back to a generic English "unit"/"units" when the ingredient has no measure
const GENERIC_UNIT_PATTERN = /^units?$/i;
// Whole-recipe amounts sent to the API: 4 decimals is enough and avoids float noise (0.1 / 0.3)
const roundRecipeAmount = (value) => Math.round(value * 10000) / 10000;
const NUTRIENT_KEYS = ["kcal", "proteins", "carbs", "fats", "fibers"];

/**
 * Per-serving nutrients multiplied by the servings eaten in the meal plan
 * @param {object} nutrients
 * @param {number} servingsEaten
 */
const nutrientsForPortion = (nutrients, servingsEaten) =>
  Object.fromEntries(
    NUTRIENT_KEYS.map((key) => [
      key,
      (Number(nutrients?.[key]) || 0) * servingsEaten,
    ])
  );

/**
 * Edit the ingredient amounts of an external recipe stored in a meal plan.
 * Client-side modal rendered over the Food modal, which stays open underneath.
 * The recipe stores ingredient amounts for the whole recipe and nutrients per serving;
 * this modal shows both for the portion in the meal plan (`quantity`).
 * Typed amounts are portion amounts: divide by amountRatio to get the whole-recipe amount the API expects.
 */
export const EditRecipeIngredientsModal = ({
  food,
  quantity,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { name, ingredients = [] } = food?.recipe || {};
  const { servingsEaten, amountRatio } = getRecipePortion(
    food?.recipe,
    quantity ?? food?.quantityNumber
  );
  const [initialAmounts] = useState(() =>
    ingredients.map(({ amount }) =>
      amount != null ? String(roundAmount(amount * amountRatio)) : ""
    )
  );
  const [amounts, setAmounts] = useState(initialAmounts);

  // Only amounts the user changed are sent, so rounding of the shown amounts never reaches the recipe
  const changedIndexes = ingredients
    .map((_ingredient, index) => index)
    .filter(
      (index) =>
        canEditRecipeIngredient(ingredients[index]) &&
        amounts[index] !== initialAmounts[index]
    );
  const hasInvalidAmount = changedIndexes.some(
    (index) => !(Number(amounts[index]) > 0)
  );
  const canSave = changedIndexes.length > 0 && !hasInvalidAmount;

  /**
   * Hand the edits to the Food modal as whole-recipe amounts; nothing is sent to the API here
   */
  const handleSave = () => {
    onSave(
      changedIndexes.map((index) => ({
        ingredientIndex: index,
        amount: roundRecipeAmount(Number(amounts[index]) / amountRatio),
      }))
    );
  };

  // preventDefault keeps the close link from navigating, which would also close the Food modal
  const handleClose = (event) => {
    event?.preventDefault();
    onClose();
  };

  /**
   * Text input (a number input shows "," in comma locales): accept "," as the
   * decimal separator but always store and show "."
   * @param {number} index
   * @param {string} rawValue
   */
  const handleAmountChange = (index, rawValue) => {
    const value = rawValue.replace(",", ".");
    if (!AMOUNT_PATTERN.test(value)) return;
    setAmounts((prevState) =>
      prevState.map((amount, i) => (i === index ? value : amount))
    );
  };

  return createPortal(
    <Modal
      className="edit-recipe-ingredients-modal"
      customTitle={<h2>{t("editRecipeIngredientsModal.title")}</h2>}
      onClose={handleClose}
    >
      <div className="body">
        <div className="recipe-info">
          <h3>{name}</h3>
          <span>
            {t("editRecipeIngredientsModal.servings", {
              count: round2(servingsEaten),
            })}
          </span>
          <span className="hint">{t("editRecipeIngredientsModal.hint")}</span>
        </div>
        <ul className="ingredients">
          {ingredients.map((ingredient, index) => {
            const {
              name: ingredientName,
              quantity: quantityText,
              unit,
              nutrients: storedNutrients = {},
            } = ingredient;
            const isEditable = canEditRecipeIngredient(ingredient);
            // Preview for the typed portion amount: scale like the API does on save (whole-recipe
            // amount), then multiply the per-serving result by the servings eaten.
            // Untouched amounts keep the stored nutrients (the shown amount is rounded).
            const isChanged = amounts[index] !== initialAmounts[index];
            const perServingNutrients =
              isEditable && isChanged
                ? scaleRecipeIngredientNutrients(
                    ingredient,
                    amounts[index] === ""
                      ? ""
                      : Number(amounts[index]) / amountRatio
                  )
                : storedNutrients;
            const nutrients = nutrientsForPortion(
              perServingNutrients,
              servingsEaten
            );
            const unitLabel =
              !unit?.trim() || GENERIC_UNIT_PATTERN.test(unit.trim())
                ? t("editRecipeIngredientsModal.defaultUnit", {
                    count: Number(amounts[index]) || 0,
                  })
                : unit;
            return (
              <li key={`${ingredient.id ?? ingredientName}-${index}`}>
                <div className="ingredient-info">
                  <span className="name">{ingredientName}</span>
                  <div className="nutrition-info">
                    <span>
                      {t("foodItems.calories")} {formatNutrient(nutrients.kcal)}
                    </span>
                    <span>&#8226;</span>
                    <span>
                      {t("foodItems.protein")}{" "}
                      {formatNutrient(nutrients.proteins)}g
                    </span>
                    <span>&#8226;</span>
                    <span>
                      {t("foodItems.carbs")} {formatNutrient(nutrients.carbs)}g
                    </span>
                    <span>&#8226;</span>
                    <span>
                      {t("foodItems.fat")} {formatNutrient(nutrients.fats)}g
                    </span>
                  </div>
                </div>
                {isEditable ? (
                  <div className="amount">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={t("editRecipeIngredientsModal.amount", {
                        name: ingredientName,
                      })}
                      value={amounts[index]}
                      onChange={(e) =>
                        handleAmountChange(index, e.target.value)
                      }
                    />
                    <span className="unit" title={unitLabel}>
                      {unitLabel}
                    </span>
                  </div>
                ) : (
                  <div className="amount">
                    <span
                      className="read-only"
                      title={t("editRecipeIngredientsModal.notEditable")}
                    >
                      {quantityText || "-"}
                    </span>
                    <span className="unit" />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <footer>
        <button type="button" data-button-type="secondary" onClick={onClose}>
          {t("editRecipeIngredientsModal.cancel")}
        </button>
        <button
          type="button"
          data-button-type="primary"
          disabled={!canSave}
          onClick={handleSave}
        >
          {t("editRecipeIngredientsModal.edit")}
        </button>
      </footer>
    </Modal>,
    document.body
  );
};

EditRecipeIngredientsModal.propTypes = {
  food: PropTypes.shape({
    recipe: PropTypes.shape({
      name: PropTypes.string,
      servings: PropTypes.number,
      macrosBase: PropTypes.string,
      ingredients: PropTypes.array,
    }),
  }).isRequired,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
